import React from "react";
import CachedIngredientSelectByCode from "../select/CachedIngredientSelectByCode";
import CachedIngredientSelectByName from "../select/CachedIngredientSelectByName";
import DateCurrentInput from "../inputs/DateCurrentInput";
import moment from "moment";
import { useAuth } from "../context/AuthContext";
import { Hash } from "lucide-react";
import { useTranslation } from "react-i18next";

interface StatusField {
    _id: string;
    code: string;
    name: string;
}

interface WorkOrderHeaderProps {
    // Document number field
    documentNumber: string;

    // Date field
    selectedDate: string;
    onDateChange: (date: string) => void;

    // Status field
    selectedStatus: StatusField | null;
    statusFields: StatusField[];
    onStatusChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;

    // Ingredient fields (selecting by ingredient, not by item)
    selectedItemId: string; // This is actually ingredient ID, but kept for backward compatibility
    onItemChange: (id: string) => void;

    // Edit mode
    isEditable?: boolean;

    // Validation
    showValidationErrors?: boolean;
    // Sub-recipe / BOM reference (Option C: user picks one sub-recipe)
    subRecipes?: Array<{text?: string; quantity: number | null; mainUnit: {symbol?: string; code?: string; name?: string} | string | null}>;
    selectedSubRecipeIndex?: number | null;
    onSubRecipeChange?: (idx: number) => void;
    ingredientVersion?: number | null;
    ingredientBatch?: number;
    ingredientWaste?: number;
    ingredientPlannedWaste?: number;
}

const WorkOrderHeader: React.FC<WorkOrderHeaderProps> = ({
    documentNumber,
    selectedDate,
    onDateChange,
    selectedStatus,
    statusFields,
    onStatusChange,
    selectedItemId,
    onItemChange,
    isEditable = false,
    showValidationErrors = false,
    subRecipes = [],
    selectedSubRecipeIndex = null,
    onSubRecipeChange,
    ingredientVersion = null,
    ingredientBatch = 0,
    ingredientWaste = 0,
    ingredientPlannedWaste = 0,
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();

    return (
        <div className="space-y-6 mb-2">
            {/* Compact Single Row - Document Info */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-2 relative z-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4">
                    {/* Idents - Ingredient Code */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                            {t("tableHeaders.idents")}
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <div className="relative z-30 [&>div]:mb-0 [&_label]:block [&_label]:!text-xs [&_label]:!font-semibold [&_label]:text-gray-700 [&_label]:mb-1.5 [&>div>div>div]:!h-10 [&>div>div>div]:!min-h-[2.5rem] [&>div>div>div]:!max-h-[2.5rem] [&_input]:!h-full [&_input]:!py-2 [&_input]:!pl-8 [&_input]:!pr-8 [&_input]:text-sm">
                            <CachedIngredientSelectByCode
                                value={selectedItemId}
                                onChange={onItemChange}
                                required={true}
                                disabled={!isEditable}
                            />
                        </div>
                        {showValidationErrors && !selectedItemId && (
                            <p className="text-red-500 text-xs mt-1">{t("validation.ingredientRequired")}</p>
                        )}
                    </div>

                    {/* Name - Ingredient Name */}
                    <div className="xl:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                            {t("tableHeaders.name")}
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <div className="relative z-20 [&>div]:mb-0 [&_label]:block [&_label]:!text-xs [&_label]:!font-semibold [&_label]:text-gray-700 [&_label]:mb-1.5 [&>div>div>div]:!h-10 [&>div>div>div]:!min-h-[2.5rem] [&>div>div>div]:!max-h-[2.5rem] [&_input]:!h-full [&_input]:!py-2 [&_input]:!pl-8 [&_input]:!pr-8 [&_input]:text-sm">
                            <CachedIngredientSelectByName
                                value={selectedItemId}
                                onChange={onItemChange}
                                required={true}
                                disabled={!isEditable}
                            />
                        </div>
                    </div>

                    {/* Document Number */}
                    <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center">
                                <Hash className="w-4 h-4 mr-2 text-blue-600" />
                                {t("tableHeaders.number")}
                            </label>
                            <div className="relative">
                                {documentNumber ? (
                                    <input
                                        type="text"
                                        value={documentNumber}
                                        readOnly
                                        className="w-full px-4 py-2 h-10 min-h-[2.5rem] max-h-[2.5rem] bg-gray-50 border border-gray-200 rounded-lg text-gray-800 font-medium focus:outline-none cursor-not-allowed"
                                    />
                                ) : (
                                    <div className="w-full px-4 py-2 h-10 min-h-[2.5rem] max-h-[2.5rem] bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg flex items-center text-blue-600">
                                        <Hash className="w-4 h-4 mr-2" />
                                        <span className="text-sm font-medium">{t("common.generatedOnSave")}</span>
                                    </div>
                                )}
                            </div>
                    </div>

                    {/* Date */}
                    <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                {t("tableHeaders.date")}
                                <span className="text-red-500 ml-1">*</span>
                            </label>
                            <div className={`relative [&_.react-datepicker-wrapper]:w-full [&_input]:w-full [&_input]:px-4 [&_input]:py-2 [&_input]:h-10 [&_input]:min-h-[2.5rem] [&_input]:max-h-[2.5rem] [&_input]:border [&_input]:rounded-lg [&_input]:focus:ring-2 [&_input]:text-gray-800 [&_input]:bg-white [&_input]:leading-normal [&_input]:border-gray-300 [&_input]:focus:ring-blue-500 [&_input]:focus:border-blue-500 [&_input]:mt-0 ${
                                showValidationErrors && !selectedDate
                                    ? '[&_input]:border-red-500 [&_input]:focus:ring-red-500 [&_input]:focus:border-red-500'
                                    : ''
                            }`}>
                                <DateCurrentInput
                                    value={selectedDate && moment(selectedDate).isValid() ? moment(selectedDate).format("DD.MM.YYYY") : ""}
                                    onChange={(val) => {
                                        const parsed = moment(val, "DD.MM.YYYY", true);
                                        if (parsed.isValid()) {
                                            onDateChange(parsed.format("YYYY-MM-DD"));
                                        } else {
                                            onDateChange("");
                                        }
                                    }}
                                    required
                                    disabled={!isEditable}
                                />
                            </div>
                    </div>

                    {/* Status */}
                    <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                {t("tableHeaders.statusLabel")}
                            </label>
                            <select
                                value={selectedStatus?._id ?? ""}
                                onChange={onStatusChange}
                                disabled={!isEditable}
                                className={`w-full px-4 py-2 h-10 min-h-[2.5rem] max-h-[2.5rem] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-800 mt-0 ${
                                    isEditable ? 'bg-white' : 'bg-gray-50 cursor-not-allowed'
                                }`}
                            >
                                {statusFields.map((status) => (
                                    <option key={status._id} value={status._id}>
                                        {status.name}
                                    </option>
                                ))}
                            </select>
                    </div>

                    {/* Referent */}
                    <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                {t("tableHeaders.referent")}
                            </label>
                            <div className="flex items-center w-full px-4 py-2 h-10 min-h-[2.5rem] max-h-[2.5rem] bg-gray-50 border border-gray-200 rounded-lg mt-0">
                                <div className="w-6 h-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white text-xs font-medium mr-3 flex-shrink-0">
                                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                                </div>
                                <span className="text-gray-700 font-medium truncate">
                                    {`${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || "N/A"}
                                </span>
                            </div>
                    </div>
                </div>
            </div>

            {/* BOM Reference Card + Sub-recipe Picker — shown when in edit mode and ingredient has sub-recipes */}
            {isEditable && subRecipes.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-800">
                            {t("workOrder.reference.title")}
                        </h3>
                        {ingredientVersion !== null && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                {t("workOrder.reference.versionLocked", { version: ingredientVersion })}
                            </span>
                        )}
                    </div>

                    {/* Read-only BOM values grid */}
                    <div className="grid grid-cols-3 gap-4 mb-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">
                                {t("tableHeaders.batch")}
                            </label>
                            <div className="w-full h-9 bg-gray-50 border border-gray-200 rounded-lg px-3 flex items-center text-sm text-gray-700">
                                {ingredientBatch || "—"}
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">
                                {t("tableHeaders.waste")}
                            </label>
                            <div className="w-full h-9 bg-gray-50 border border-gray-200 rounded-lg px-3 flex items-center text-sm text-gray-700">
                                {ingredientWaste || "—"}
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">
                                {t("tableHeaders.wasteKg")}
                            </label>
                            <div className="w-full h-9 bg-gray-50 border border-gray-200 rounded-lg px-3 flex items-center text-sm text-gray-700">
                                {ingredientPlannedWaste ? `${ingredientPlannedWaste} kg` : "—"}
                            </div>
                        </div>
                    </div>

                    {/* Sub-recipe picker */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                            {t("workOrder.subRecipe.label")}
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <select
                            value={selectedSubRecipeIndex !== null ? selectedSubRecipeIndex : ""}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val !== "" && onSubRecipeChange) {
                                    onSubRecipeChange(parseInt(val, 10));
                                }
                            }}
                            className={`w-full h-10 border rounded-lg px-3 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-white ${
                                showValidationErrors && selectedSubRecipeIndex === null
                                    ? "border-red-500 focus:ring-red-500/20 focus:border-red-500"
                                    : "border-gray-300"
                            }`}
                        >
                            <option value="">{t("workOrder.subRecipe.placeholder")}</option>
                            {subRecipes.map((sr, i) => {
                                const unitSymbol = typeof sr.mainUnit === 'object' && sr.mainUnit !== null
                                    ? (sr.mainUnit.symbol || sr.mainUnit.code || sr.mainUnit.name || "")
                                    : (typeof sr.mainUnit === 'string' ? sr.mainUnit : "");
                                return (
                                    <option key={i} value={i}>
                                        {sr.text} — {sr.quantity ?? ''} {unitSymbol}
                                    </option>
                                );
                            })}
                        </select>
                        {showValidationErrors && selectedSubRecipeIndex === null && (
                            <p className="text-xs text-red-500 mt-1">
                                {t("errors.workOrder.missingSubRecipe")}
                            </p>
                        )}
                    </div>
                </div>
            )}


        </div>
    );
};

export default WorkOrderHeader;
